import { useCallback, useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import FlowModal from "../components/FlowModal.jsx";
import ResultModal from "../components/ResultModal.jsx";
import Shimmer from "../components/Shimmer.jsx";
import LtvBadge from "../components/LtvBadge.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useDeposits } from "../hooks/useDeposits.js";
import { useLoan } from "../hooks/useLoans.js";
import RepayChoiceModal from "../components/RepayChoiceModal.jsx";
import { fetchLoanActivity } from "../services/api.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import { formatUsdcCompact, liveLoanInterest } from "../utils/format.js";
import { chainNowSec } from "../utils/chainClock.js";

const TOKEN_SYMBOL = "USDC";

/**
 * Loan detail page reached from the "View More" action on the My Loans
 * table. Mirrors TdDetail's visual language — dark forest-green panels,
 * cream + gold accents — so navigation between deposit and loan pages
 * stays visually consistent.
 *
 * Data sources:
 *   - `useDeposits` for the underlying deposit (collateral, maturity).
 *   - `useLoan(depositId)` for live loan principal / accrued interest / LTV
 *     coming through the backend's `/api/lending/loans/:depositId` endpoint
 *     (falls back to direct on-chain reads when the backend is offline).
 */
export default function LoanDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const depositId = Number(id);
    const idIsValid = Number.isFinite(depositId) && depositId > 0;
    // §V6.2 — `/loans/turbo` (and any other non-numeric segment) used
    // to fall through to this component and render "Loan Not Found
    // for Term Deposit #NaN". The Turbo route was retired in V6.2;
    // catch invalid IDs here and redirect to the 404 page so the
    // user gets a real not-found rather than a confused stub.
    if (id !== undefined && !idIsValid) {
        return <Navigate to="/404" replace />;
    }
    const { address, signer } = useWallet();
    const { deposits, isLoading: depositsLoading, refresh } = useDeposits(address);
    const { loan: liveLoan, isLoading: loanLoading, refresh: refreshLoan } = useLoan(
        idIsValid ? depositId : null
    );

    const deposit = useMemo(
        () => deposits.find((d) => d.id === depositId) || null,
        [deposits, depositId]
    );

    const [busy, setBusy] = useState(false);
    const [flowOpen, setFlowOpen] = useState(false);
    const [steps, setSteps] = useState([]);
    const [result, setResult] = useState(null);
    const [activity, setActivity] = useState([]);
    // Repay-choice modal: { loan, principal, accruedInterest, accrual }
    // or null. Built from the page's own `liveLoan` snapshot — no fresh
    // chain read needed since the contract recomputes the exact debt
    // inside `repayFull` at execution time.
    const [repayChoice, setRepayChoice] = useState(null);

    // Loan activity timeline — every draw / repayment / netting /
    // liquidation for this deposit, from the backend's event log.
    const loadActivity = useCallback(() => {
        if (!idIsValid) return;
        fetchLoanActivity(depositId)
            .then((data) => {
                // Newest event first — backend returns ascending chain
                // order, but the user reads top-down and wants the most
                // recent draw / repayment at the top of the list.
                const items = [...(data?.items || [])].sort((a, b) => {
                    const bb = Number(b.blockNumber || 0) - Number(a.blockNumber || 0);
                    if (bb !== 0) return bb;
                    return Number(b.logIndex || 0) - Number(a.logIndex || 0);
                });
                setActivity(items);
            })
            .catch(() => setActivity([]));
    }, [idIsValid, depositId]);

    useEffect(() => {
        loadActivity();
    }, [loadActivity]);

    // The backend's GET /api/lending/loans/:id already returns
    // `accruedInterest` with the live-up-to-the-second `pendingInterest`
    // folded in (lending.controller.js → getByDeposit). Polling
    // refreshLoan every 10s keeps the figure fresh without any direct
    // chain reads from the frontend.
    useEffect(() => {
        if (!idIsValid) return undefined;
        const handle = setInterval(() => {
            refreshLoan({ background: true });
        }, 10_000);
        return () => clearInterval(handle);
    }, [idIsValid, refreshLoan]);

    // 1-second ticker so the Debt Snapshot's Accrued / Total Owed cards
    // advance visibly between the 10s API polls. The figure is built
    // off the loan's `storedAccruedInterest + lastAccruedAt + aprBps`
    // and extrapolated per-second (liveLoanInterest helper) so it
    // matches what the repay modal computes on click.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    /**
     * Step 1 of repay — opens the choice modal sourced from the same
     * `liveLoan` snapshot that the Debt Snapshot card on this page
     * shows. Sharing the source guarantees the modal's Interest line
     * matches the page behind it instead of diverging because the modal
     * did its own chain read while the page used the backend snapshot.
     * `repayFull` re-derives the exact debt on-chain at execution time,
     * so any few-second drift between display and block time is
     * absorbed by the contract.
     */
    function openRepayChoice() {
        if (!signer || !idIsValid || !liveLoan) return;
        const principal = liveLoan.principal;
        const accruedInterest = liveLoanInterest(liveLoan); // chain-time default
        if (principal + accruedInterest === 0n) {
            setResult({
                type: "error",
                title: "Nothing to repay",
                message: "This loan is already closed."
            });
            return;
        }
        setRepayChoice({
            loan: { id: depositId },
            principal,
            accruedInterest,
            accrual: liveLoan
        });
    }

    /**
     * Step 2 of repay — runs the chosen approve + repay. `isFull` routes
     * to `repayFull` (re-reads the debt in-tx so no dust is left); a
     * partial amount routes to `repayLoan` and leaves the rest open.
     */
    async function runRepay(loan, amountWei, isFull) {
        if (!signer || amountWei <= 0n) return;
        setRepayChoice(null);
        setBusy(true);
        setSteps([
            { key: "approve", label: `Approve ${formatUsdcCompact(amountWei)} USDC`, status: "loading" },
            { key: "pay", label: isFull ? "Full Settlement" : "Pay Now", status: "pending" }
        ]);
        setFlowOpen(true);
        setResult(null);
        try {
            const lending = getContract("lending", signer);
            const overrides = await getTxOverrides(signer.provider);

            // Ask Lending directly which USDC it'll pull from — guards
            // against a stale VITE_USDC_ADDRESS that would otherwise have
            // us approving the wrong token.
            const assetAddress = await lending.asset();
            const usdc = new ethers.Contract(
                assetAddress,
                ["function allowance(address,address) view returns (uint256)", "function approve(address,uint256) returns (bool)"],
                signer
            );
            const ownerAddr = await signer.getAddress();
            const allowance = await usdc.allowance(ownerAddr, config.contracts.lending);
            if (allowance < amountWei) {
                const approveTx = await usdc.approve(config.contracts.lending, ethers.MaxUint256, overrides);
                await approveTx.wait();
                // Race guard: `approveTx.wait()` only means the dApp's
                // provider saw the approval — the RPC the WALLET uses to
                // simulate the repay (eth_estimateGas) can still be a
                // block behind, reading allowance as 0 and reverting with
                // "MockUSDC: allowance". Poll until it's actually visible.
                await waitForAllowance(usdc, ownerAddr, config.contracts.lending, amountWei);
            }
            setSteps((prev) => prev.map((s) =>
                s.key === "approve" ? { ...s, status: "done" } : s.key === "pay" ? { ...s, status: "loading" } : s
            ));

            const tx = isFull
                ? await lending.repayFull(loan.id, overrides)
                : await lending.repayLoan(loan.id, amountWei, overrides);
            await tx.wait();
            setSteps((prev) => prev.map((s) => (s.key === "pay" ? { ...s, status: "done" } : s)));

            await new Promise((r) => setTimeout(r, 300));
            setFlowOpen(false);
            setResult(
                isFull
                    ? {
                          type: "success",
                          title: "Loan Closed",
                          message: `Term Deposit #${loan.id} loan paid in full — nothing left outstanding.`
                      }
                    : {
                          type: "success",
                          title: "Payment Applied",
                          message: `Repaid ${formatUsdcCompact(amountWei)} USDC against Term Deposit #${loan.id}'s loan. You can clear the remaining balance anytime.`
                      }
            );
            await Promise.all([refresh(), refreshLoan()]);
            // Give the indexer a beat, then refresh the activity timeline.
            setTimeout(loadActivity, 1500);
        } catch (err) {
            setSteps((prev) => prev.map((s) => (s.status === "loading" ? { ...s, status: "error" } : s)));
            await new Promise((r) => setTimeout(r, 300));
            setFlowOpen(false);
            setResult({
                type: "error",
                title: "Pay Failed",
                message: decodeContractError(err)
            });
        } finally {
            setBusy(false);
        }
    }

    if (!address) {
        return (
            <div className="-mt-6 flex min-h-[60vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Wallet Required</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">Connect your wallet to view this loan.</p>
                </div>
            </div>
        );
    }

    const initialLoading = depositsLoading || loanLoading;
    if (initialLoading && !deposit && !liveLoan) {
        return <LoanDetailSkeleton depositId={depositId} />;
    }

    if (!deposit && !liveLoan) {
        return (
            <div className="-mt-6 flex min-h-[40vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Loan Not Found</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">
                        No loan attached to Term Deposit #{depositId}, or this wallet doesn&rsquo;t own it.
                    </p>
                    <Link
                        to="/loans"
                        className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#FBC206] px-4 py-2 text-[12px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all"
                    >
                        Back to My Loans
                        <ChevronRight />
                    </Link>
                </div>
            </div>
        );
    }

    // Loan principal + live accrued. Backend already overlays pending
    // interest at fetch time, but it's a snapshot frozen between 10s
    // polls — we extrapolate per-second client-side off the loan record's
    // `storedAccruedInterest + lastAccruedAt + aprBps` so the Debt
    // Snapshot card ticks visibly and matches the repay modal (which
    // computes its own pending fresh on click).
    const principal = liveLoan?.principal ?? BigInt(deposit?.loanBalance || 0);
    // tick is referenced so React's lint catches that this expression
    // depends on it (the actual freshness comes from the chain clock —
    // liveLoanInterest's chainNowMs() default — re-read on each re-render
    // driven by the 1s tick state above).
    void tick;
    // §V6.2 — synthesize from the deposit row when the backend Loan
    // doc isn't ready yet. `liveLoanInterest`'s own missing-field
    // fallbacks take it from there, so the Debt Snapshot ticks
    // immediately instead of sitting on the chain-stored value.
    const accrualSource = liveLoan || (deposit
        ? {
              principal: BigInt(deposit.loanBalance || 0),
              storedAccruedInterest: BigInt(deposit.loanAccruedInterest || 0)
          }
        : null);
    const accrued = accrualSource
        ? liveLoanInterest(accrualSource) // chain-time default
        : 0n;
    const totalOwed = principal + accrued;
    const isOpen = liveLoan ? liveLoan.isOpen : principal > 0n;

    const depositPrincipal = deposit ? BigInt(deposit.principal) : 0n;
    // §V6.2 — Safe Vault % is per-product (Standard 80%, Turbo 50%).
    // Prefer the chain-stored `safeVaultAmount` (immutable at open)
    // so the figure is the exact amount the contract sees; fall back
    // to a productMode-derived bps when the indexer hasn't synced
    // the field yet. Routing now matches Standard (80/10/9/1) for both
    // products, so the Safe Vault slice is 80% regardless of product.
    const productSafeVaultBps = 8000n;
    const safeVaultPctLabel = 80;
    const collateral = deposit && BigInt(deposit.safeVaultAmount || 0) > 0n
        ? BigInt(deposit.safeVaultAmount)
        : (depositPrincipal * productSafeVaultBps) / 10000n;
    const maxBorrow = (collateral * BigInt(config.protocol.maxLtvBps)) / 10000n;
    const remCapacity = maxBorrow > principal ? maxBorrow - principal : 0n;
    const ltvBps = collateral > 0n ? Number((totalOwed * 10000n) / collateral) : 0;
    const ltvPct = Math.round(ltvBps / 100);
    const liqPct = config.protocol.liquidationThresholdBps / 100;
    const isLiquidatable = ltvBps >= config.protocol.liquidationThresholdBps;
    // Liquidation only after the term ends → badge green in-term, then
    // orange/red. Mirrors the loan-table colouring.
    const termEnded = deposit?.maturityTime
        ? chainNowSec() >= Number(deposit.maturityTime)
        : false;

    const explorerBase = config.network.blockExplorerUrl?.replace(/\/+$/, "");
    const explorerLendingUrl = explorerBase && config.contracts.lending
        ? `${explorerBase}/address/${config.contracts.lending}`
        : null;

    const openedAt = liveLoan?.openedAt ? new Date(liveLoan.openedAt) : null;
    const idPadded = String(depositId).padStart(6, "0");

    return (
        <div className="-mt-6 space-y-3">
            {/* Header — title comes from the global breadcrumb. We just
                surface the status pill + actions here. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <StatePill isOpen={isOpen} isLiquidatable={isLiquidatable} />
                <div className="flex items-center gap-2">
                    {explorerLendingUrl ? (
                        <a
                            href={explorerLendingUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#10281b] px-3 py-1 text-[11px] font-semibold text-white ring-1 ring-[#bfb67b] hover:bg-[#162e1f]"
                        >
                            Lending Contract
                            <ExternalIcon />
                        </a>
                    ) : null}
                    {isOpen ? (
                        <button
                            type="button"
                            onClick={openRepayChoice}
                            disabled={busy || !signer || !liveLoan}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#FBC206] px-3.5 py-1 text-[11px] font-semibold text-[#10281b] hover:bg-[#e6b005] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Pay Now
                            <ChevronRight />
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Hero strip — debt + ltv */}
            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 text-white flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Debt Snapshot</h3>
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-[#FBC206]">
                            APR {config.protocol.loanAprBps / 100}%
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        <Metric label="Principal" value={`$${formatUsdcCompact(principal)}`} hint="Outstanding" />
                        <Metric label="Accrued" value={`$${formatUsdcCompact(accrued)}`} hint="Live" accent />
                        <Metric label="Total Owed" value={`$${formatUsdcCompact(totalOwed)}`} hint="Pay-off amount" bold />
                    </div>
                </div>

                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 flex flex-col">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">LTV vs Liquidation</h3>
                        <LtvBadge ltv={ltvPct} termEnded={termEnded} />
                    </div>
                    <div className="mt-3">
                        <div className="font-manrope text-[24px] font-bold text-white">{ltvPct}%</div>
                        <div className="mt-0.5 text-[10px] text-[#e6c985]/70">
                            Liquidation threshold: {liqPct}% — buffer {Math.max(0, liqPct - ltvPct)}%
                        </div>
                    </div>
                    <div className="relative mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10 ring-1 ring-[#bfb67b]">
                        <div
                            className="h-full rounded-full transition-all"
                            style={{
                                width: `${Math.min(100, ltvPct)}%`,
                                backgroundColor:
                                    ltvBps >= config.protocol.liquidationThresholdBps
                                        ? "#C93030"
                                        : ltvBps >= config.protocol.maxLtvBps
                                        ? "#FBC206"
                                        : "#22C55E"
                            }}
                        />
                        <div
                            className="absolute inset-y-0 w-px bg-[#e6c985]/70"
                            style={{ left: `${Math.min(100, liqPct)}%` }}
                            aria-hidden
                        />
                    </div>
                </div>
            </div>

            {/* Collateral + capacity grid */}
            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 flex flex-col">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Collateral</h3>
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                            Safe Vault
                        </span>
                    </div>
                    <ul className="mt-4 space-y-3">
                        <KvRow label="Deposit Principal" value={`$${formatUsdcCompact(depositPrincipal)}`} />
                        <KvRow label={`Safe Vault Slice (${safeVaultPctLabel}%)`} value={`$${formatUsdcCompact(collateral)}`} />
                        <KvRow label={`Borrow Cap (${config.protocol.maxLtvBps / 100}% LTV)`} value={`$${formatUsdcCompact(maxBorrow)}`} />
                        <KvRow label="Remaining Capacity" value={`$${formatUsdcCompact(remCapacity)}`} last />
                    </ul>
                </div>

                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Loan Info</h3>
                    <ul className="mt-3 divide-y divide-white/10">
                        <InfoRow label="Loan ID" value={`#${idPadded}`} />
                        <InfoRow label="Borrower" value={shortAddr(deposit?.owner || address)} mono />
                        <InfoRow label="Status" value={isOpen ? "Open" : "Closed"} />
                        <InfoRow label="APR" value={`${config.protocol.loanAprBps / 100}%`} />
                        <InfoRow
                            label="Opened"
                            value={openedAt ? openedAt.toLocaleString() : <span className="text-[#e6c985]/60">unknown</span>}
                        />
                        <InfoRow
                            label="Liquidation Threshold"
                            value={`${liqPct}% LTV`}
                        />
                        <InfoRow last label="Network" value={config.network.name} />
                    </ul>
                </div>
            </div>

            {/* Loan activity timeline — every draw / repayment / netting */}
            <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Loan Activity</h3>
                <p className="mt-1 text-[10px] text-[#e6c985]/65">
                    Every borrow draw and repayment against this deposit. A deposit can
                    draw its capacity in multiple chunks — each shows as its own row.
                </p>
                <ul className="mt-3 space-y-2">
                    {activity.length === 0 ? (
                        <li className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-4 text-center text-[11px] text-[#e6c985]/70">
                            No loan activity indexed yet.
                        </li>
                    ) : null}
                    {activity.map((a) => {
                        const inflow = a.direction === "in";
                        // §H.3.1 self-paying netting gets its own visual
                        // treatment — it isn't an inflow or an outflow
                        // for the borrower; it's an INTERNAL accounting
                        // offset where bond yield cancels loan interest.
                        // Rendering it like a normal repay misleads the
                        // user into thinking they paid USDC.
                        const isNetting = a.label === "Interest Netted";
                        // §I.6 — loans drawn during a CYR pool sweep are
                        // marked `autoCompound` by the backend so the row
                        // can be tagged distinctly. They're outflows of
                        // BORROW CAPACITY, not USDC, from the user's POV.
                        const isAutoCompound = Boolean(a.autoCompound);
                        const tone = isNetting
                            ? {
                                  ring: "ring-[#FBC206]/60",
                                  bg: "bg-[#FBC206]/10",
                                  amount: "text-[#FBC206]"
                              }
                            : isAutoCompound
                            ? {
                                  ring: "ring-[#22C55E]/60",
                                  bg: "bg-[#22C55E]/10",
                                  amount: inflow ? "text-[#22C55E]" : "text-[#FBC206]"
                              }
                            : {
                                  ring: "ring-[#bfb67b]",
                                  bg: "bg-white/10",
                                  amount: inflow ? "text-[#22C55E]" : "text-[#FBC206]"
                              };
                        return (
                            <li
                                key={`${a.txHash}-${a.logIndex}`}
                                className={`flex items-center justify-between rounded-[10px] ring-1 px-3 py-2.5 ${tone.bg} ${tone.ring}`}
                            >
                                <div>
                                    <div className="flex items-center gap-2">
                                        <div className="font-manrope text-[12px] font-bold text-white">
                                            {a.label}
                                        </div>
                                        {isNetting ? (
                                            <span className="rounded-full bg-[#FBC206]/25 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest text-[#FBC206]">
                                                Internal
                                            </span>
                                        ) : null}
                                        {isAutoCompound ? (
                                            <span className="rounded-full bg-[#22C55E]/25 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest text-[#22C55E]">
                                                Automated Reinvestment
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="mt-0.5 text-[10px] text-[#e6c985]/60">
                                        {isNetting ? (
                                            <span className="mr-2 text-[#e6c985]/75">
                                                Bond yield offset loan interest — no USDC moved.
                                            </span>
                                        ) : null}
                                        {isAutoCompound ? (
                                            <span className="mr-2 text-[#e6c985]/75">
                                                Drawn by a Turbo pool sweep
                                                {a.autoCompoundPoolId
                                                    ? ` (Pool #${a.autoCompoundPoolId})`
                                                    : ""}.
                                            </span>
                                        ) : null}
                                        {a.timestamp
                                            ? new Date(a.timestamp).toLocaleString()
                                            : `block ${a.blockNumber}`}
                                        {config.network.blockExplorerUrl && a.txHash ? (
                                            <>
                                                {"  ·  "}
                                                <a
                                                    href={`${config.network.blockExplorerUrl}/tx/${a.txHash}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-[#FBC206] hover:underline"
                                                >
                                                    {shortAddr(a.txHash)}
                                                </a>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                                <div className={`font-manrope text-[13px] font-bold ${tone.amount}`}>
                                    {isNetting
                                        ? `↔ $${formatUsdcCompact(a.amount)}`
                                        : `${inflow ? "+" : "−"}$${formatUsdcCompact(a.amount)} ${TOKEN_SYMBOL}`}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Capital flow explainer */}
            <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">How this loan works</h3>
                <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <FlowCard
                        title="Source"
                        body="Capital came from THIS deposit's Safe Vault slice, not from anyone else's. Per-deposit isolation enforced on-chain."
                    />
                    <FlowCard
                        title="Interest"
                        body={`Your bond keeps earning while borrowed. The contract nets that yield against your ${config.protocol.loanAprBps / 100}% loan interest daily, so the accrued figure above is already offset by bond yield. Repayment stays optional while the bond is active.`}
                    />
                    <FlowCard
                        title="Repay"
                        body="Click Pay Now to settle in a single transaction. The contract auto-computes exact owed (no dust)."
                    />
                    <FlowCard
                        title="Default risk"
                        body={`If LTV reaches ${liqPct}%, anyone can liquidate. Your outstanding debt plus a liquidator bonus is paid out of this deposit's Safe Vault collateral, and any remaining collateral is returned to you.`}
                    />
                </ul>
            </div>

            {deposit ? (
                <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Linked Term Deposit</h3>
                        <Link
                            to={`/term-deposits/${depositId}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#FBC206] px-3 py-1 text-[10px] font-semibold text-[#10281b] hover:bg-[#e6b005]"
                        >
                            Open Deposit
                            <ChevronRight />
                        </Link>
                    </div>
                    <p className="mt-2 text-[11px] text-[#e6c985]/80">
                        This loan is collateralised against Term Deposit #{idPadded}. While the loan
                        is open, settlement and early-exit on the deposit are blocked.
                    </p>
                </div>
            ) : null}

            <RepayChoiceModal
                state={repayChoice}
                busy={busy}
                onClose={() => setRepayChoice(null)}
                onConfirm={runRepay}
            />
            <FlowModal open={flowOpen} title={`Pay Loan #${idPadded}`} steps={steps} />
            <ResultModal
                open={Boolean(result)}
                type={result?.type}
                title={result?.title}
                message={result?.message}
                primaryAction={
                    result?.type === "success" ? { label: "Back to Loans", to: "/loans" } : undefined
                }
                onClose={() => setResult(null)}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function StatePill({ isOpen, isLiquidatable }) {
    if (!isOpen) {
        return (
            <span className="rounded-full bg-[#1D3E29] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#e6c985]/80">
                Closed
            </span>
        );
    }
    if (isLiquidatable) {
        return (
            <span className="rounded-full bg-[#C93030] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                At Risk
            </span>
        );
    }
    return (
        <span className="rounded-full bg-[#FBC206] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#10281b]">
            Open
        </span>
    );
}

function Metric({ label, value, hint, accent, bold }) {
    return (
        <div className="rounded-[10px] bg-white/5 ring-1 ring-[#bfb67b]/30 px-3 py-2">
            <div className={`text-[10px] font-semibold uppercase tracking-widest ${accent ? "text-[#FBC206]" : "text-[#e6c985]/80"}`}>
                {label}
            </div>
            <div className={`mt-1 font-manrope ${bold ? "text-[20px] font-bold" : "text-[16px] font-semibold"} text-white`}>
                {value}
            </div>
            <div className="text-[10px] text-[#e6c985]/60">{hint}</div>
        </div>
    );
}

function KvRow({ label, value, last }) {
    return (
        <li className={`flex items-center justify-between ${last ? "" : "border-b border-white/10 pb-2"}`}>
            <span className="text-[11px] text-[#e6c985]/80">{label}</span>
            <span className="font-manrope text-[12px] font-semibold text-white">{value}</span>
        </li>
    );
}

function InfoRow({ label, value, mono, last }) {
    return (
        <li className={`flex items-center justify-between gap-3 ${last ? "py-2" : "py-2"}`}>
            <span className="text-[11px] text-[#e6c985]/80">{label}</span>
            <span className={`text-[12px] text-white ${mono ? "font-mono" : "font-manrope font-semibold"}`}>
                {value}
            </span>
        </li>
    );
}

function FlowCard({ title, body }) {
    return (
        <div className="rounded-[10px] bg-white/5 ring-1 ring-[#bfb67b]/30 px-3 py-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#FBC206]">{title}</div>
            <p className="mt-1 text-[11px] leading-snug text-white/85">{body}</p>
        </div>
    );
}

function LoanDetailSkeleton({ depositId }) {
    return (
        <div className="-mt-6 space-y-3">
            <div className="flex items-center justify-between">
                <Shimmer width="10rem" height="1.25rem" />
                <Shimmer width="6rem" height="1.5rem" />
            </div>
            <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 h-32" />
            <div className="grid gap-3 lg:grid-cols-12">
                <div className="lg:col-span-5 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 h-48" />
                <div className="lg:col-span-7 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 h-48" />
            </div>
            <p className="text-center text-[11px] text-[#10281b]/60">Loading loan #{depositId}…</p>
        </div>
    );
}

function shortAddr(addr) {
    if (!addr) return "—";
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Polls the USDC allowance until it covers `required` — used after an
 * approve tx so the follow-up repay isn't simulated against a stale
 * block where the allowance still reads 0. Best-effort: returns false
 * on timeout and lets the caller proceed anyway.
 */
async function waitForAllowance(usdc, owner, spender, required, timeoutMs = 20000, intervalMs = 1000) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        try {
            const current = await usdc.allowance(owner, spender);
            if (current >= required) return true;
        } catch {
            // node not caught up yet — keep polling
        }
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    return false;
}

function ChevronRight() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}

function ExternalIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 3h6v6" />
            <path d="M17 3 10 10" />
            <path d="M17 12v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4" />
        </svg>
    );
}
